import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DateService } from 'Services';
import {
    getUser,
    shouldShowManageTenants,
    shouldShowMerchants,
    shouldShowExecutionOrder,
    shouldShowTenantGroups
} from 'Selectors';
import Overview from 'Components/Overview';

import styles from './RevisionOverview.css';

type Props = {
    data?: Deployment;
    scheduledOverview?: boolean;
};

const RevisionOverview = ({ data, scheduledOverview }: Props) => {
    /**
     * Temp function to show the `scheduled deploy button` with dropdown.
     * If `deployed` is defined as a query string, the button will be displayed.
     */
    const { t } = useTranslation();
    const { landlordId, tenantKey } = useSelector(getUser);
    const { version }: { version: string } = useParams();

    if (!data) return null;
    const { acquirerSummary, merchantSummary, tenantGroupSummary } = data.summary;
    const deployedAt = DateService.getDateTime(data?.createdAt);
    const showExecutionOrder = shouldShowExecutionOrder();
    const overviewLeft: RevisionOverviewColumn[] = [
        {
            id: 'deployedAt',
            label: t('Deployed'),
            data: deployedAt,
            className: styles.overviewDeployedDate
        },
        {
            id: 'createdBy',
            label: t('Deployed By'),
            data: data?._formatted?.createdBy,
            className: styles.overviewDeployedBy
        },
        {
            id: 'comment',
            label: t('Comment'),
            data: data?.comment,
            className: styles.overviewComment
        },
        {
            id: 'newGlobalRules',
            label: t('New Global Rules'),
            data: acquirerSummary?.addedRules,
            className: styles.newGlobalRules
        },
        ...(showExecutionOrder
            ? [
                  {
                      id: 'modifiedExecutionOrder',
                      label: t('Modified Execution Order'),
                      data: acquirerSummary?.modifiedExecutionOrder ? 'Yes' : 'No',
                      className: styles.modifiedExecutionOrder
                  }
              ]
            : []),
        {
            id: 'modifiedGlobalRules',
            label: t('Modified Global Rules'),
            data: acquirerSummary?.modifiedRules,
            className: styles.modifiedGlobalRules
        }
    ];

    if (shouldShowMerchants()) {
        const merchantRelatedOverview: RevisionOverviewColumn[] = [
            {
                id: 'newMerchants',
                label: t('New Merchants'),
                data: merchantSummary?.added || 0,
                className: styles.newdMerchants
            },
            {
                id: 'modifiedMerchants',
                label: t('Modified Merchants'),
                data: (merchantSummary?.modified || 0) + (merchantSummary?.disabled || 0),
                className: styles.modifiedMerchants
            },
            {
                id: 'deactivatedMerchants',
                label: t('Deactivated Merchants'),
                data: merchantSummary?.disabled || 0,
                className: styles.deactivatedMerchants
            }
        ];
        overviewLeft.push(...merchantRelatedOverview);
    }

    if (shouldShowManageTenants(landlordId, tenantKey) && shouldShowTenantGroups()) {
        const tenantGroupRelatedOverview: RevisionOverviewColumn[] = [
            {
                id: 'newTenantGroups',
                label: t('New Tenant Groups'),
                data: tenantGroupSummary?.added || 0,
                className: styles.newTenantGroups
            },
            {
                id: 'modifiedTenantGroups',
                label: t('Modified Tenant Groups'),
                data: tenantGroupSummary?.modified || tenantGroupSummary?.removed,
                className: styles.modifiedTenantGroups
            }
        ];

        overviewLeft.push(...tenantGroupRelatedOverview);
    }

    return (
        <span className={styles.revisionOverview}>
            <Overview
                dataTestId='revision-overview'
                leftTitle={`${t('versionNum', { version })} ${t('Overview')}`}
                leftData={overviewLeft}
                deployment={scheduledOverview ? data : undefined}
            />
        </span>
    );
};

export default RevisionOverview;
